import { z } from "zod";
import { assetViewSchema } from "./studio.js";

const timestampSchema = z.iso.datetime();
const sharedAssetKindSchema = z.enum(["image", "video", "audio"]);
const titleSchema = z.string().trim().min(1).max(160);
const descriptionSchema = z.string().trim().max(2_000);
const promptContentSchema = z.string().trim().min(1).max(20_000);

export const promptTemplateViewSchema = z.strictObject({
  id: z.uuid(),
  title: titleSchema,
  content: promptContentSchema,
  trashedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
});

export const sharedLibraryItemViewSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    id: z.uuid(),
    kind: z.literal("asset"),
    title: titleSchema,
    description: descriptionSchema,
    authorName: z.string().min(1).max(160),
    canWithdraw: z.boolean(),
    sourceAssetId: z.uuid().optional(),
    media: z.strictObject({
      kind: sharedAssetKindSchema,
      mimeType: z.string().min(1).max(255),
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      durationSeconds: z.number().finite().nonnegative().optional()
    }),
    createdAt: timestampSchema
  }),
  z.strictObject({
    id: z.uuid(),
    kind: z.literal("prompt"),
    title: titleSchema,
    description: descriptionSchema,
    authorName: z.string().min(1).max(160),
    canWithdraw: z.boolean(),
    promptContent: promptContentSchema,
    createdAt: timestampSchema
  })
]);

export const createPromptTemplateRequestSchema = z.strictObject({
  title: titleSchema,
  content: promptContentSchema
});

export const updatePromptTemplateRequestSchema = z
  .strictObject({
    title: titleSchema.optional(),
    content: promptContentSchema.optional()
  })
  .refine((input) => input.title !== undefined || input.content !== undefined);

export const publishAssetRequestSchema = z.strictObject({
  assetId: z.uuid(),
  title: titleSchema,
  description: descriptionSchema.default("")
});

export const publishPromptRequestSchema = z.strictObject({
  templateId: z.uuid(),
  description: descriptionSchema.default("")
});

export const promptTemplateListQuerySchema = z.strictObject({
  view: z.enum(["active", "trash"]).default("active")
});

export const promptTemplateListResponseSchema = z.strictObject({
  templates: z.array(promptTemplateViewSchema)
});

export const sharedLibraryListQuerySchema = z.strictObject({
  kind: z.enum(["asset", "prompt"]).optional(),
  q: z.string().trim().min(1).max(255).optional(),
  cursor: z.string().min(1).max(2_048).optional(),
  limit: z.coerce.number().int().positive().max(100).default(50)
});

export const sharedLibraryListResponseSchema = z.strictObject({
  items: z.array(sharedLibraryItemViewSchema),
  nextCursor: z.string().min(1).max(2_048).optional()
});

export const sharedImportResponseSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("asset"), asset: assetViewSchema }),
  z.strictObject({
    kind: z.literal("prompt"),
    template: promptTemplateViewSchema
  })
]);

export type PromptTemplateView = z.infer<typeof promptTemplateViewSchema>;
export type SharedLibraryItemView = z.infer<
  typeof sharedLibraryItemViewSchema
>;
export type CreatePromptTemplateRequest = z.infer<
  typeof createPromptTemplateRequestSchema
>;
export type UpdatePromptTemplateRequest = z.infer<
  typeof updatePromptTemplateRequestSchema
>;
export type PublishAssetRequest = z.infer<typeof publishAssetRequestSchema>;
export type PublishPromptRequest = z.infer<typeof publishPromptRequestSchema>;
export type PromptTemplateListQuery = z.infer<
  typeof promptTemplateListQuerySchema
>;
export type PromptTemplateListResponse = z.infer<
  typeof promptTemplateListResponseSchema
>;
export type SharedLibraryListQuery = z.infer<
  typeof sharedLibraryListQuerySchema
>;
export type SharedLibraryListResponse = z.infer<
  typeof sharedLibraryListResponseSchema
>;
export type SharedImportResponse = z.infer<typeof sharedImportResponseSchema>;
